import React, { useEffect, useState } from 'react';
import {
  Button,
  Switch,
  FormControlLabel,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Stack,
  Card,
  Typography,
} from '@mui/material';
import { Alert } from '@mui/material';
import { Edit } from '@mui/icons-material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import LoadingState from '../../components/ui/LoadingState';
import api from '../../api';

// Chaves usadas pela plataforma (as booleanas viram interruptores).
const CHAVES_CONHECIDAS = [
  { key: 'JOB_ABONO_MENSALIDADE', padrao: 'false', tipo: 'bool', description: 'Dia 1º: abona a mensalidade do mês anterior de quem não jogou e não pagou, e muda para diarista.' },
  { key: 'AVALIACAO_ATIVA', padrao: 'true', tipo: 'bool', description: 'Abre a avaliação dos jogadores 30 min após o fim da pelada, por 24 horas.' },
  { key: 'EMAIL_PARTIDAS_PELADA', padrao: 'true', tipo: 'bool', description: 'Ao encerrar a pelada, envia a quem jogou o e-mail com o histórico das partidas (times, placar, lances e força).' },
  { key: 'EMAIL_RESULTADO_PELADA', padrao: 'true', tipo: 'bool', description: 'Ao encerrar a avaliação, envia a quem jogou o e-mail com o resultado (crack, bola murcha, números da pelada).' },
  { key: 'EMAIL_RESUMO_MENSAL', padrao: 'true', tipo: 'bool', description: 'Dia 1º às 9h: e-mail aos mensalistas com o resumo do mês anterior (números da pelada, os dele, melhores parceiros e adversário mais difícil).' },
  { key: 'DURACAO_PELADA_MINUTOS', padrao: '150', tipo: 'numero', description: 'Duração usada para considerar a pelada encerrada quando a diretoria não a fecha.' },
  { key: 'HORARIO_INICIO_CHECKIN', padrao: '19:00', description: 'Horário (HH:mm, Brasília) em que o botão "Chegou" é liberado no dia da pelada. A diretoria pode registrar antes.' },
  { key: 'CHECKIN_NFC_ATIVO', padrao: 'false', tipo: 'bool', description: 'Check-in encostando o celular na tag NFC da concentração (link da tag em Administração › Check-in). Vale o horário de início do check-in.' },
  { key: 'CHECKIN_NFC_VALIDAR_LOCAL', padrao: 'true', tipo: 'bool', description: 'Check-in por NFC também exige a localização dentro do perímetro (Administração › Check-in). Desligado, basta encostar na tag.' },
  {
    key: 'CHECKIN_BOTAO_CHEGOU_ATIVO',
    padrao: 'true',
    tipo: 'bool',
    confirmarAoDesligar: 'Os jogadores deixam de ver o botão "Chegou" e passam a ver "Check-in por NFC". Admin e Diretor continuam com o botão para registrar quem está sem celular (e os convidados).',
    description: 'Botão "Chegou" para os jogadores. Desligado, o check-in é só pela tag NFC (a diretoria continua com o botão). Também é desligado automaticamente na pelada em que alguém fizer check-in pela tag, até ela ser encerrada.',
  },
  { key: 'VALOR_MENSALIDADE', padrao: '80', tipo: 'numero', description: 'Valor da mensalidade.' },
  { key: 'VALOR_DIARIA', padrao: '20', tipo: 'numero', description: 'Valor da diária (diaristas e convidados).' },
  {
    key: 'APP_ANTIGO_ATIVO',
    padrao: 'true',
    tipo: 'bool',
    confirmarAoDesligar: 'O web antigo deixa de conseguir entrar, confirmar presença e fazer check-in (as rotas antigas passam a ser bloqueadas). Este app continua funcionando normalmente, pois usa rotas próprias.',
    description: 'Web antigo: rotas antigas de login, confirmação de presença e check-in. Desligue para bloquear o app antigo; este app não é afetado.',
  },
  { key: 'NOTIFICACAO_ONESIGNAL', padrao: 'true', tipo: 'bool', description: 'Envia as notificações também pelo OneSignal (além da lista no app).' },
  { key: 'NOTIFICACAO_PUSH_NATIVO', padrao: 'true', tipo: 'bool', description: 'Envia as notificações como push nativo para os aparelhos que ativaram (celular/computador).' },
];

// Aviso manual da diretoria para todos os jogadores (lista do app, OneSignal e push nativo)
// Resumo do mês anterior (e-mail do dia 1º): a diretoria recebe um teste antes de todo mundo
const TesteResumoMensal = ({ onMessage }) => {
  const [enviando, setEnviando] = useState(false);
  const enviar = async () => {
    setEnviando(true);
    try {
      const { data } = await api.post('/admin/resumo-mensal/teste', {});
      onMessage(data.enviados ? 'Resumo do mês anterior enviado para o seu e-mail.' : 'Não houve pelada no mês anterior: nada a enviar.', data.enviados ? 'success' : 'info');
    } catch (error) {
      onMessage(error.response?.data?.error || 'Não foi possível enviar o teste.', 'error');
    }
    setEnviando(false);
  };
  return (
    <Card sx={{ p: 2 }}>
      <Typography variant="subtitle1">Resumo do mês</Typography>
      <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1.5 }}>
        No dia 1º, os mensalistas recebem por e-mail o resumo do mês anterior. Envie um teste só para você para conferir.
      </Typography>
      <Button variant="outlined" onClick={enviar} disabled={enviando}>{enviando ? 'Enviando...' : 'Enviar teste para mim'}</Button>
    </Card>
  );
};

const EnviarAviso = ({ onMessage }) => {
  const [mensagem, setMensagem] = useState('');
  const [confirmar, setConfirmar] = useState(false);
  const [enviando, setEnviando] = useState(false);

  const enviar = async () => {
    setEnviando(true);
    try {
      await api.post('/sendNotification', { message: mensagem.trim() });
      setMensagem('');
      onMessage('Aviso enviado para todos os jogadores.', 'success');
    } catch (error) {
      onMessage(error.response?.data?.error || 'Não foi possível enviar o aviso.', 'error');
    }
    setEnviando(false);
    setConfirmar(false);
  };

  return (
    <Card sx={{ p: 2 }}>
      <Typography variant="subtitle1">Enviar aviso para todos</Typography>
      <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1.5 }}>
        Aparece na lista de notificações do app e vai pelos canais ligados acima (OneSignal e push nativo).
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-start' }}>
        <TextField size="small" fullWidth multiline maxRows={4} placeholder="Ex.: Hoje tem churrasco depois da pelada!" value={mensagem} onChange={(e) => setMensagem(e.target.value)} inputProps={{ maxLength: 240 }} />
        <Button variant="contained" onClick={() => setConfirmar(true)} disabled={!mensagem.trim()} sx={{ flexShrink: 0 }}>Enviar</Button>
      </Stack>
      <Dialog open={confirmar} onClose={() => setConfirmar(false)} fullWidth maxWidth="xs">
        <DialogTitle>Enviar para todos?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{mensagem.trim()}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmar(false)} color="inherit">Cancelar</Button>
          <Button onClick={enviar} variant="contained" disabled={enviando}>Enviar aviso</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

const ConfigurationManager = () => {
  const [configurations, setConfigurations] = useState([]);
  const [peladaActive, setPeladaActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [newConfig, setNewConfig] = useState({ key: '', value: '', description: '' });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [desligar, setDesligar] = useState(null); // { chave, existente } aguardando confirmação

  useEffect(() => {
    fetchConfigurations();
    fetchPeladaActive();
  }, []);

  const fetchConfigurations = async () => {
    try {
      const response = await api.get('/getConfigurations');
      setConfigurations(response.data.configurations);
    } catch (error) {
      console.error('Error fetching configurations:', error);
      setSnackbarMessage('Erro ao buscar configurações');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const fetchPeladaActive = async () => {
    try {
      const response = await api.get('/getPeladaActive');
      setPeladaActive(response.data.data);
    } catch (error) {
      console.error('Error fetching pelada active:', error);
      setSnackbarMessage('Erro ao buscar pelada ativa');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleAddOrUpdateConfig = async () => {
    try {
      const url = '/configuration';
      const method = editingConfig ? 'edit' : 'create';
      console.log(editingConfig)
      setNewConfig({...newConfig, action:method});
      console.log(newConfig)

      const payload = editingConfig ? { ...newConfig, id: editingConfig._id, action:method} : { ...newConfig, action:method};
      console.log(payload)
     
      await api.post(url,payload)

      setSnackbarMessage(editingConfig ? 'Configuração atualizada' : 'Configuração adicionada');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      fetchConfigurations();
      setDialogOpen(false);
      setNewConfig({ key: '', value: '', description: '' });
      setEditingConfig(null);
    } catch (error) {
      console.error('Error saving configuration:', error);
      setSnackbarMessage('Erro ao salvar configuração');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const salvarChave = async (key, value, description, existente) => {
    try {
      const payload = existente
        ? { key, value, description: existente.description || description, id: existente._id, action: 'edit' }
        : { key, value, description, action: 'create' };
      await api.post('/configuration', payload);
      setSnackbarMessage('Configuração salva');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      fetchConfigurations();
    } catch (error) {
      setSnackbarMessage('Erro ao salvar configuração');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleEditConfig = (config) => {
    setEditingConfig(config);
    setNewConfig({ key: config.key, value: config.value, description: config.description });
    setDialogOpen(true);
  };

  const handleOpenGuestToggle = async () => {
    try {
      setLoading(true);
      await api.post('/openGuest', { peladaId:peladaActive._id });
      setSnackbarMessage('Convidados Liberados');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      fetchPeladaActive();
    } catch (error) {
      console.error('Error updating pelada active:', error);
      setSnackbarMessage('Erro ao atualizar pelada ativa');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  return (
    <Stack spacing={2}>
      {loading ? (
        <LoadingState />
      ) : (
        <>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2}>
            {peladaActive ? (
              <Card sx={{ px: 2, py: 1 }}>
                <FormControlLabel
                  control={<Switch checked={Boolean(peladaActive.openGuest)} onChange={handleOpenGuestToggle} />}
                  label="Permitir convidados na pelada aberta"
                />
              </Card>
            ) : (
              <Typography variant="body2" color="text.secondary">Nenhuma pelada aberta no momento.</Typography>
            )}
            <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => { setEditingConfig(null); setNewConfig({ key: '', value: '', description: '' }); setDialogOpen(true); }}>
              Nova configuração
            </Button>
          </Stack>

          <Card sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Automações e parâmetros da plataforma</Typography>
            <Stack divider={<span style={{ borderTop: '1px solid #E6E8EC', display: 'block' }} />}>
              {CHAVES_CONHECIDAS.map((chave) => {
                const existente = configurations.find((c) => c.key === chave.key);
                const valor = existente ? existente.value : chave.padrao;
                return (
                  <Stack key={chave.key} direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} sx={{ py: 1.25 }}>
                    <div style={{ flexGrow: 1 }}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{chave.key}</Typography>
                      <Typography variant="caption" color="text.secondary">{chave.description}{!existente && ' (usando o padrão)'}</Typography>
                    </div>
                    {chave.tipo === 'bool' ? (
                      <Switch
                        checked={String(valor).toLowerCase() === 'true'}
                        onChange={(e) => {
                          const ligar = e.target.checked;
                          if (!ligar && chave.confirmarAoDesligar) setDesligar({ chave, existente });
                          else salvarChave(chave.key, ligar ? 'true' : 'false', chave.description, existente);
                        }}
                      />
                    ) : (
                      <Button size="small" variant="outlined" onClick={() => (existente ? handleEditConfig(existente) : (setEditingConfig(null), setNewConfig({ key: chave.key, value: chave.padrao, description: chave.description }), setDialogOpen(true)))}>
                        {existente ? `${valor} · editar` : 'Definir'}
                      </Button>
                    )}
                  </Stack>
                );
              })}
            </Stack>
          </Card>

          <EnviarAviso onMessage={(message, severity) => { setSnackbarMessage(message); setSnackbarSeverity(severity); setSnackbarOpen(true); }} />
          <TesteResumoMensal onMessage={(message, severity) => { setSnackbarMessage(message); setSnackbarSeverity(severity); setSnackbarOpen(true); }} />

          <TableContainer component={Card}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Chave</TableCell>
                  <TableCell>Valor</TableCell>
                  <TableCell>Descrição</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {configurations.map((config) => (
                  <TableRow key={config._id} hover>
                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{config.key}</TableCell>
                    <TableCell>{config.value}</TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{config.description}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleEditConfig(config)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="xs">
            <DialogTitle>{editingConfig ? 'Editar configuração' : 'Nova configuração'}</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ pt: 1 }}>
                <TextField
                  autoFocus
                  label="Chave"
                  value={newConfig.key}
                  onChange={(e) => setNewConfig({ ...newConfig, key: e.target.value })}
                  disabled={!!editingConfig}
                  fullWidth
                />
                <TextField label="Valor" value={newConfig.value} onChange={(e) => setNewConfig({ ...newConfig, value: e.target.value })} fullWidth />
                <TextField label="Descrição" multiline minRows={2} value={newConfig.description} onChange={(e) => setNewConfig({ ...newConfig, description: e.target.value })} fullWidth />
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={() => setDialogOpen(false)} color="inherit">Cancelar</Button>
              <Button onClick={handleAddOrUpdateConfig} variant="contained">{editingConfig ? 'Atualizar' : 'Adicionar'}</Button>
            </DialogActions>
          </Dialog>

          <Dialog open={Boolean(desligar)} onClose={() => setDesligar(null)} fullWidth maxWidth="xs">
            <DialogTitle>Desligar {desligar?.chave.key}?</DialogTitle>
            <DialogContent>
              <Typography variant="body2">{desligar?.chave.confirmarAoDesligar}</Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={() => setDesligar(null)} color="inherit">Cancelar</Button>
              <Button
                color="error"
                variant="contained"
                onClick={() => {
                  salvarChave(desligar.chave.key, 'false', desligar.chave.description, desligar.existente);
                  setDesligar(null);
                }}
              >
                Desligar
              </Button>
            </DialogActions>
          </Dialog>

          <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
            <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} variant="filled">
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </>
      )}
    </Stack>
  );
};

export default ConfigurationManager;
